import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { OpportunityService } from '../../services/opportunity.service';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Opportunity, OpportunityCreateDTO } from '../../interfaces/crm/crm.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import SearchableSelect from '../common/SearchableSelect';

interface OpportunityFormProps {
  open: boolean;
  opportunity?: Opportunity | null;
  onClose: () => void;
  onSaved: (opportunity: Opportunity) => void;
}

const OpportunityForm: React.FC<OpportunityFormProps> = ({ open, opportunity, onClose, onSaved }) => {
  const toast = useToast();
  const isEdit = Boolean(opportunity);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [title, setTitle] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [estimatedAmount, setEstimatedAmount] = useState('');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(opportunity?.title ?? '');
    setCustomerId(opportunity?.customerId ?? '');
    setEstimatedAmount(opportunity?.estimatedAmount != null ? String(opportunity.estimatedAmount) : '');
    setExpectedCloseDate(opportunity?.expectedCloseDate ?? '');
    setNotes(opportunity?.notes ?? '');
  }, [open, opportunity]);

  useEffect(() => {
    if (!open) return;
    const institutionId = AuthService.getSelectedInstitution();
    CustomerService.listByInstitution(institutionId, 0, 200, isEdit ? 'ALL' : undefined) // FW-1: edit keeps a now-inactive current customer
      .then(res => setCustomers(res.content))
      .catch(e => toast.showApiError(e, 'Failed to load customers'));
  }, [open, toast]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const canSubmit = title.trim() !== '' && customerId !== '' && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution();
      const amount = estimatedAmount.trim() === '' ? undefined : Number(estimatedAmount);
      const dto: OpportunityCreateDTO = {
        title: title.trim(),
        customerId,
        estimatedAmount: Number.isFinite(amount as number) ? amount : undefined,
        expectedCloseDate: expectedCloseDate || undefined,
        notes: notes.trim() || undefined,
        institutionId,
      };
      const result = isEdit && opportunity
        ? await OpportunityService.update(opportunity.id, dto)
        : await OpportunityService.create(dto);
      toast.showSuccess(isEdit ? 'Opportunity updated' : 'Opportunity created');
      onSaved(result);
      onClose();
    } catch (e) {
      toast.showApiError(e, isEdit ? 'Failed to update opportunity' : 'Failed to create opportunity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-help-page="crm.opportunityForm.page">
      <DialogTitle>{isEdit ? 'Edit Opportunity' : 'New Opportunity'}</DialogTitle>
      <DialogContent>
        <Box data-help-section="crm.opportunityForm.section.details" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            data-help="crm.opportunityForm.field.title"
            label="Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
          />
          <SearchableSelect
            data-help="crm.opportunityForm.field.customer"
            label="Customer"
            required
            options={customers}
            value={customerId}
            onChange={id => setCustomerId(id)}
            getOptionLabel={c => c.name}
          />
          <TextField
            data-help="crm.opportunityForm.field.amount"
            label="Estimated Amount"
            type="number"
            value={estimatedAmount}
            onChange={e => setEstimatedAmount(e.target.value)}
            inputProps={{ step: 0.0001, min: 0 }}
          />
          <TextField
            data-help="crm.opportunityForm.field.closeDate"
            label="Expected Close Date"
            type="date"
            value={expectedCloseDate}
            onChange={e => setExpectedCloseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            data-help="crm.opportunityForm.field.notes"
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="crm.opportunityForm.cancel" onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button data-help="crm.opportunityForm.save" variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save Changes' : 'Create Opportunity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpportunityForm;
